import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID, NgZone, signal, input, output, computed } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { CartResponseData } from '../../../../core/models/order-payload.model';
import { OrderProcessingService } from '../../../../core/services/order-processing';
import { environment } from '../../../../../environments/environment';

declare global {
    interface Window {
        responseHandler: (response: any) => void;
    }
}

@Component({
    selector: 'app-payment',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './payment.html',
    styleUrl: './payment.scss',
})
export class Payment implements OnInit, OnDestroy {
    isBrowser = false;
    tokenValue: string = '';
    descriptorValue: string = '';
    last4digits: string = '';
    errorMessages: string[] = [];
    paymentSuccess = false;
    cartDataInput = input<any>(null);
    internalCartData = signal<any>(null);
    cartData = computed(() => this.isModal() ? this.cartDataInput() : this.internalCartData());

    isModal = input<boolean>(false);
    paymentSuccessEvent = output<any>();
    paymentErrorEvent = output<string>();

    apiLoginID = environment.apiLoginID;
    clientKey = environment.clientKey;

    private scriptElement: HTMLScriptElement | null = null;

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        private ngZone: NgZone,
        private router: Router,
        public orderProcessing: OrderProcessingService
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    ngOnInit(): void {
        if (this.isBrowser) {
            this.loadAcceptUIScript();
            this.registerResponseHandler();
            if (!this.isModal()) {
                this.loadCartData();
            }
        }
    }

    ngOnDestroy(): void {
        if (this.isBrowser) {
            // Clean up the global handler
            delete (window as any).responseHandler;
            // Remove the script
            if (this.scriptElement && this.scriptElement.parentNode) {
                this.scriptElement.parentNode.removeChild(this.scriptElement);
            }
        }
    }

    private loadAcceptUIScript(): void {
        if (typeof document === 'undefined') return;
        // Check if script is already loaded
        if (document.querySelector('script[src*="AcceptUI.js"]')) {
            return;
        }
        this.scriptElement = document.createElement('script');
        this.scriptElement.src = environment.acceptJSLink;
        this.scriptElement.charset = 'utf-8';
        this.scriptElement.type = 'text/javascript';
        document.body.appendChild(this.scriptElement);
    }

    private registerResponseHandler(): void {
        window.responseHandler = (response: any) => {
            this.ngZone.run(() => {
                if (this.orderProcessing.isUploading()) {
                    return;
                }

                if (response.messages.resultCode === 'Error') {
                    this.errorMessages = [];
                    for (let i = 0; i < response.messages.message.length; i++) {
                        this.errorMessages.push(
                            response.messages.message[i].code + ': ' + response.messages.message[i].text
                        );
                    }
                    this.paymentSuccess = false;
                } else {
                    this.errorMessages = [];
                    this.tokenValue = response.opaqueData.dataValue;
                    this.descriptorValue = response.opaqueData.dataDescriptor;
                    this.last4digits = response.encryptedCardData?.cardNumber?.slice(-4) || '';

                    // Show global loader and call cart-invoice-payment endpoint
                    this.orderProcessing.isUploading.set(true);
                    this.submitPayment();
                }
            });
        };
    }

    private async submitPayment(): Promise<void> {
        try {
            let cartId = '';
            let customerName = '';
            let email = '';
            let zipCode = '';

            const currentData: any = this.cartData();
            if (currentData) {
                cartId = currentData.id || '';
                const address = currentData.address;
                if (address) {
                    customerName = `${address.firstname || ''} ${address.lastname || ''}`.trim();
                    email = address.email || '';
                    zipCode = address.zipcode || '';
                }
            }

            // Fallback for standalone usage
            if (!cartId && !this.isModal()) {
                const savedOrder = localStorage.getItem('full_order');
                if (savedOrder) {
                    const parsed = JSON.parse(savedOrder);
                    const orderData = parsed?.data || parsed;
                    cartId = cartId || orderData?.id || '';
                    const address = orderData?.address;
                    if (address && !customerName) {
                        customerName = `${address.firstname || ''} ${address.lastname || ''}`.trim();
                        email = address.email || '';
                        zipCode = address.zipcode || '';
                    }
                }

                if (!cartId) {
                    const savedCart = localStorage.getItem('cart_response');
                    if (savedCart) {
                        try {
                            const parsed = JSON.parse(savedCart);
                            cartId = parsed?.data?.id || '';
                        } catch (e) {
                            console.error('Failed to parse cart_response from local storage', e);
                        }
                    }
                }
            }

            const res = await this.orderProcessing.submitCartInvoicePayment(cartId, {
                paymentToken: this.tokenValue,
                last4: this.last4digits,
                customerName,
                email,
                zipCode
            });
            
            this.paymentSuccess = true;

            if (this.isModal()) {
                this.paymentSuccessEvent.emit(res);
            } else {
                // Clear all localStorage
                localStorage.clear();
                // Save payment response in new localStorage
                localStorage.setItem('payment_response', JSON.stringify(res));
                // Redirect to invoice page
                this.router.navigate(['/invoice']);
            }

        } catch (error: any) {
            console.error('Payment API error:', error);
            const serverMsg = error?.error?.error || error?.message || 'Payment processing failed. Please try again.';
            if (this.isModal()) {
                this.paymentErrorEvent.emit(serverMsg);
            } else {
                this.errorMessages.push(serverMsg);
            }
        } finally {
            this.orderProcessing.isUploading.set(false);
        }
    }

    private loadCartData(): void {
        let loaded = false;
        const savedOrder = localStorage.getItem('full_order');
        if (savedOrder) {
            try {
                const parsed = JSON.parse(savedOrder);
                const data = parsed?.data || parsed;
                if (data && data.cartItems) {
                    this.internalCartData.set(data);
                    loaded = true;
                }
            } catch (e) {
                console.error('Failed to parse full_order from local storage', e);
            }
        }

        if (!loaded) {
            const savedCart = localStorage.getItem('cart_response');
            if (savedCart) {
                try {
                    const parsed = JSON.parse(savedCart);
                    const data = parsed?.data || parsed;
                    if (data && data.cartItems) {
                        this.internalCartData.set(data);
                        loaded = true;
                    }
                } catch (e) {
                    console.error('Failed to parse cart_response from local storage', e);
                }
            }
        }
    }
}
